'use client';

import { useState } from 'react';
import { 
  Settings, 
  Bell, 
  Shield, 
  Database, 
  Mail, 
  Clock,
  Save,
  RefreshCw
} from 'lucide-react';
import { Header } from '@/components/header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function SettingsPage() {
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => setIsSaving(false), 1000);
  };

  return (
    <div className="min-h-screen bg-[#0A192F]">
      <Header 
        title="系统设置" 
        subtitle="配置系统参数和偏好设置"
        showSearch={false}
        showActions={false}
      />

      <div className="p-8">
        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="bg-[#112240] border border-[#1d3557]">
            <TabsTrigger value="general" className="data-[state=active]:bg-[#1d3557] data-[state=active]:text-[#64FFDA]">
              <Settings className="w-4 h-4 mr-2" />
              常规
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-[#1d3557] data-[state=active]:text-[#64FFDA]">
              <Bell className="w-4 h-4 mr-2" />
              通知
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-[#1d3557] data-[state=active]:text-[#64FFDA]">
              <Shield className="w-4 h-4 mr-2" />
              安全
            </TabsTrigger>
            <TabsTrigger value="system" className="data-[state=active]:bg-[#1d3557] data-[state=active]:text-[#64FFDA]">
              <Database className="w-4 h-4 mr-2" />
              系统
            </TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-6">
            <Card className="bg-[#112240] border-[#1d3557]">
              <CardHeader>
                <CardTitle className="text-[#E6F1FF]">图书馆信息</CardTitle>
                <CardDescription className="text-[#8892B0]">
                  设置图书馆的基本信息
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>图书馆名称</Label>
                    <Input 
                      className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                      defaultValue="智图图书馆"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>联系电话</Label>
                    <Input 
                      className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                      defaultValue="400-123-4567"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>地址</Label>
                  <Input 
                    className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                    defaultValue="北京市朝阳区图书馆路1号"
                  />
                </div>
                <div className="space-y-2">
                  <Label>开放时间</Label>
                  <Input 
                    className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                    defaultValue="周一至周日 9:00-21:00"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#112240] border-[#1d3557]">
              <CardHeader>
                <CardTitle className="text-[#E6F1FF]">借阅规则</CardTitle>
                <CardDescription className="text-[#8892B0]">
                  配置默认借阅规则
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>默认借阅期限（天）</Label>
                    <Input 
                      type="number"
                      className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                      defaultValue="30"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>最大续借次数</Label>
                    <Input 
                      type="number"
                      className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                      defaultValue="2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>逾期罚款（元/天）</Label>
                    <Input 
                      type="number"
                      className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                      defaultValue="1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notification Settings */}
          <TabsContent value="notifications" className="space-y-6">
            <Card className="bg-[#112240] border-[#1d3557]">
              <CardHeader>
                <CardTitle className="text-[#E6F1FF]">通知设置</CardTitle>
                <CardDescription className="text-[#8892B0]">
                  配置系统通知方式
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-[#0A192F]">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-[#64FFDA]" />
                    <div>
                      <p className="text-[#E6F1FF]">邮件通知</p>
                      <p className="text-sm text-[#8892B0]">通过邮件发送逾期提醒</p>
                    </div>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-[#0A192F]">
                  <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-[#64FFDA]" />
                    <div>
                      <p className="text-[#E6F1FF]">系统通知</p>
                      <p className="text-sm text-[#8892B0]">在系统内显示通知</p>
                    </div>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="space-y-2">
                  <Label>提前提醒天数</Label>
                  <Select defaultValue="3">
                    <SelectTrigger className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#112240] border-[#1d3557]">
                      <SelectItem value="1">1天</SelectItem>
                      <SelectItem value="3">3天</SelectItem>
                      <SelectItem value="7">7天</SelectItem>
                      <SelectItem value="14">14天</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Settings */}
          <TabsContent value="security" className="space-y-6">
            <Card className="bg-[#112240] border-[#1d3557]">
              <CardHeader>
                <CardTitle className="text-[#E6F1FF]">安全设置</CardTitle>
                <CardDescription className="text-[#8892B0]">
                  配置系统安全参数
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-[#0A192F]">
                  <div>
                    <p className="text-[#E6F1FF]">登录验证</p>
                    <p className="text-sm text-[#8892B0]">启用双重身份验证</p>
                  </div>
                  <Switch />
                </div>
                <div className="space-y-2">
                  <Label>会话超时时间（分钟）</Label>
                  <Input 
                    type="number"
                    className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                    defaultValue="30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>密码有效期（天）</Label>
                  <Input 
                    type="number"
                    className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                    defaultValue="90"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* System Settings */}
          <TabsContent value="system" className="space-y-6">
            <Card className="bg-[#112240] border-[#1d3557]">
              <CardHeader>
                <CardTitle className="text-[#E6F1FF]">系统维护</CardTitle>
                <CardDescription className="text-[#8892B0]">
                  数据备份与系统维护
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-[#0A192F]">
                  <div className="flex items-center gap-3">
                    <Database className="w-5 h-5 text-[#64FFDA]" />
                    <div>
                      <p className="text-[#E6F1FF]">自动备份</p>
                      <p className="text-sm text-[#8892B0]">每天凌晨自动备份数据</p>
                    </div>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-[#0A192F]">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#64FFDA]" />
                    <div>
                      <p className="text-[#E6F1FF]">自动清理</p>
                      <p className="text-sm text-[#8892B0]">自动清理过期日志</p>
                    </div>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="pt-4 flex gap-4">
                  <Button variant="outline" className="border-[#1d3557] text-[#8892B0]">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    立即备份
                  </Button>
                  <Button variant="outline" className="border-[#1d3557] text-[#8892B0]">
                    导出数据
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#112240] border-[#1d3557]">
              <CardHeader>
                <CardTitle className="text-[#E6F1FF]">系统信息</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-2 border-b border-[#1d3557]">
                    <span className="text-[#8892B0]">系统版本</span>
                    <span className="text-[#E6F1FF]">v2.1.0</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#1d3557]">
                    <span className="text-[#8892B0]">最后更新</span>
                    <span className="text-[#E6F1FF]">2024-01-20</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#1d3557]">
                    <span className="text-[#8892B0]">数据库状态</span>
                    <span className="text-[#10B981]">正常</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-[#8892B0]">服务器状态</span>
                    <span className="text-[#10B981]">运行中</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Save Button */}
        <div className="flex justify-end pt-6 border-t border-[#1d3557]">
          <Button 
            className="bg-[#64FFDA] text-[#0A192F] hover:bg-[#64FFDA]/90"
            onClick={handleSave}
            disabled={isSaving}
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? '保存中...' : '保存设置'}
          </Button>
        </div>
      </div>
    </div>
  );
}
